import React, { useState, useEffect } from 'react';
import {
  faTruck,
  faCheckCircle,
  faHourglassHalf,
  faFileExport,
  faSearch,
  faBox,
  faCalendar,
  faMapMarkerAlt,
  faUser,
  faFileAlt,
  faSpinner,
  faTimesCircle,
  faImage,
  faCheck,
  faClock,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import BASE_URL from '../../api';

const DASHBOARD_COUNT_API = `${BASE_URL}/api/getctrl/getdashboardcount`;
const TRACKING_API = `${BASE_URL}/api/onlinebookings/track`;

const Dashboard = () => {
  const [metrics, setMetrics] = useState({
    totalShipments: 0,
    onTimeDeliveries: 0,
    pendingShipments: 0,
    podUploaded: 0,
    podPending: 0,
  });
  const [docketNumber, setDocketNumber] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [error, setError] = useState(null);

  const token = () => localStorage.getItem('token');

  // Fetch Dashboard Counts
  const fetchMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const t = token();
      if (!t) throw new Error('Login required');

      const res = await fetch(DASHBOARD_COUNT_API, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${t}`,
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) throw new Error(`Dashboard: ${res.status}`);
      const json = await res.json();

      if (json.success && json.data) {
        setMetrics({
          totalShipments: json.data.totalShipments || 0,
          onTimeDeliveries: json.data.onTimeDeliveries || 0,
          pendingShipments: json.data.pendingShipments || 0,
          podUploaded: json.data.podUploaded || 0,
          podPending: json.data.podPending || 0,
        });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Tracking Data
  const fetchTracking = async () => {
    if (!docketNumber.trim()) {
      setError('Please enter a docket number');
      return;
    }

    setLoading(true);
    setError(null);
    setTrackingData(null);

    const t = token();
    if (!t) {
      setError('Login required');
      setLoading(false);
      return;
    }

    const params = new URLSearchParams();
    params.append('docketnumber', docketNumber.trim());

    try {
      const res = await fetch(TRACKING_API, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${t}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params,
      });

      // Check the Content-Type header to see if it's actually JSON
      const contentType = res.headers.get("content-type");

      if (contentType && contentType.indexOf("application/json") !== -1) {
        const json = await res.json();

        if (json.success && json.data) {
          setTrackingData(json.data);
        } else {
          // This shows your custom message if the API says "success: false"
          setError(json.message || 'Invalid docket number or no data found');
        }
      } else {
        // If the server returns HTML (the <!DOCTYPE error), show your friendly message
        setError('Invalid docket number or no data found');
      }

    } catch (e) {
      // This catches network failures or total crashes
      setError('Connection error. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  // Metric Cards
  const metricCards = [
    { title: 'Total Shipments', value: metrics.totalShipments, icon: faTruck, grad: 'from-blue-500 to-cyan-500' },
    // { title: 'On-Time Deliveries', value: metrics.onTimeDeliveries, icon: faCheckCircle, grad: 'from-green-500 to-emerald-500' },
    { title: 'Pending Shipments', value: metrics.pendingShipments, icon: faHourglassHalf, grad: 'from-yellow-500 to-amber-500' },
    { title: 'POD Pending', value: metrics.podPending, icon: faFileExport, grad: 'from-red-500 to-rose-500' },
    { title: 'POD Uploaded', value: metrics.podUploaded, icon: faFileAlt, grad: 'from-purple-500 to-pink-500' },
  ];

  // Status Styling & Icon
  const getStatusStyle = (status) => {
    const s = (status || '').toString().toLowerCase();
    let bg = 'bg-gray-100 text-gray-700';
    let icon = faExclamationTriangle;
    let iconColor = 'text-gray-600';

    if (s.includes('delivered') || s.includes('pod uploaded')) {
      bg = 'bg-green-100 text-green-800';
      icon = faCheck;
      iconColor = 'text-green-600';
    } else if (s.includes('transit') || s.includes('in progress')) {
      bg = 'bg-blue-100 text-blue-800';
      icon = faTruck;
      iconColor = 'text-blue-600';
    } else if (s.includes('pending') || s.includes('hold')) {
      bg = 'bg-yellow-100 text-yellow-800';
      icon = faHourglassHalf;
      iconColor = 'text-yellow-600';
    }

    return { bg, icon, iconColor };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50">
      <div className="flex">
        <div className="flex-1 p-6 lg:p-10">

          {/* Header */}
          <div className="mb-10">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              <FontAwesomeIcon icon={faTruck} className="text-orange-600" />
              Logistics Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Real-time tracking & analytics</p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-2">
              <FontAwesomeIcon icon={faTimesCircle} />
              {error}
            </div>
          )}

          {/* Metrics */}
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {loading
                ? Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <div
                      key={i}
                      className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 animate-pulse"
                    >
                      <div className="h-12 w-12 bg-gray-200 rounded-full mb-4"></div>
                      <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                      <div className="h-8 bg-gray-200 rounded w-16"></div>
                    </div>
                  ))
                : metricCards.map((c, i) => (
                  <div
                    key={i}
                    className="group relative overflow-hidden bg-white/90 backdrop-blur-md rounded-2xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${c.grad} opacity-10 group-hover:opacity-20 transition-opacity`}
                    ></div>
                    <div className="relative flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{c.title}</p>
                        <p className="text-3xl font-bold text-gray-800 mt-1">{c.value}</p>
                      </div>
                      <div
                        className={`p-4 rounded-2xl bg-gradient-to-br ${c.grad} text-white shadow-md`}
                      >
                        <FontAwesomeIcon icon={c.icon} className="text-xl" />
                      </div>
                    </div>
                    <div className="mt-3 h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full bg-gradient-to-r ${c.grad} transition-all duration-700`}
                        style={{ width: `${Math.min((c.value / 50) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
            </div>
          </div>


          {/* Tracking */}
          <div className="max-w-auto mx-auto">
            <div className="bg-white/95 backdrop-blur-lg rounded-3xl shadow-xl p-8 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-gradient-to-br from-orange-500 to-pink-500 rounded-2xl text-white shadow-lg">
                  <FontAwesomeIcon icon={faSearch} className="text-xl" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Track Shipment</h3>
                  <p className="text-gray-600">Enter docket number (e.g., DC000001)</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <input
                  type="text"
                  value={docketNumber}
                  onChange={(e) => setDocketNumber(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchTracking()}
                  placeholder="DC000001"
                  className="flex-1 px-5 py-4 border border-gray-300 rounded-2xl focus:outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-500 transition-all text-lg font-medium"
                  disabled={trackingLoading}
                />
                <button
                  onClick={fetchTracking}
                  disabled={trackingLoading}
                  className="px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-600 text-white font-semibold rounded-2xl hover:from-orange-600 hover:to-pink-700 focus:outline-none focus:ring-4 focus:ring-orange-300 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg"
                >
                  {trackingLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSearch} />
                      Track
                    </>
                  )}
                </button>
              </div>

              {/* Result */}
              {trackingLoading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse"></div>
                  ))}
                </div>
              ) : trackingData ? (
                <div className="mt-6 p-6 bg-gradient-to-r from-gray-50 to-orange-50 rounded-2xl border border-gray-200">

                  {/* Docket + Status Badge */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <h4 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                      <FontAwesomeIcon icon={faFileAlt} className="text-orange-600" />
                      {trackingData.docket}
                    </h4>

                    {/* Enhanced Status Badge */}
                    {trackingData.docketstatus && (() => {
                      const { bg, icon, iconColor } = getStatusStyle(trackingData.docketstatus);
                      return (
                        <div className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm ${bg}`}>
                          <FontAwesomeIcon icon={icon} className={`${iconColor}`} />
                          <span>{trackingData.docketstatus}</span>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-gray-700">

                    {trackingData.bookingdate && (
                      <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={faCalendar} className="text-orange-600" />
                        <div>
                          <p className="text-sm text-gray-500">Booking Date</p>
                          <p className="font-semibold">{new Date(trackingData.bookingdate).toLocaleDateString()}</p>
                        </div>
                      </div>
                    )}

                    {trackingData.consigneeid && (
                      <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={faUser} className="text-orange-600" />
                        <div>
                          <p className="text-sm text-gray-500">Consignee</p>
                          <p className="font-semibold">{trackingData.consigneeid.companyName}</p>
                        </div>
                      </div>
                    )}

                    {trackingData.originlocation && (
                      <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-orange-600" />
                        <div>
                          <p className="text-sm text-gray-500">Origin</p>
                          <p className="font-semibold">{trackingData.originlocation.areaname}, {trackingData.originlocation.hub}</p>
                        </div>
                      </div>
                    )}

                    {trackingData.destlocation && (
                      <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-orange-600" />
                        <div>
                          <p className="text-sm text-gray-500">Destination</p>
                          <p className="font-semibold">{trackingData.destlocation.areaname}, {trackingData.destlocation.hub}</p>
                        </div>
                      </div>
                    )}

                    {trackingData.grandtotal != null && (
                      <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={faBox} className="text-orange-600" />
                        <div>
                          <p className="text-sm text-gray-500">Grand Total</p>
                          <p className="font-semibold">₹{trackingData.grandtotal}</p>
                        </div>
                      </div>
                    )}

                    {trackingData.createdAt && (
                      <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={faCalendar} className="text-orange-600" />
                        <div>
                          <p className="text-sm text-gray-500">Created</p>
                          <p className="font-semibold">{new Date(trackingData.createdAt).toLocaleString()}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* POD Image */}
                  {trackingData.file && (
                    <div className="mt-6">
                      <p className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-2">
                        <FontAwesomeIcon icon={faImage} className="text-orange-600" />
                        Proof of Delivery (POD)
                      </p>
                      <img
                        src={`${BASE_URL}/${trackingData.file.replace(/\\/g, '/')}`}
                        alt="POD"
                        className="max-w-full rounded-lg shadow-md border border-gray-200"
                        onError={(e) => (e.currentTarget.src = 'https://via.placeholder.com/600x400?text=POD+Not+Found')}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FontAwesomeIcon icon={faSearch} className="text-4xl mb-3 text-gray-300" />
                  <p className="text-lg">Enter a docket number to track</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;